import { ampli } from '@/ampli';

import { useLiquidityScore } from '@/hooks/useLiquidityScore';

import { fontStyles } from '@/styles/typography';

import { isTokenBlockedByTokenList } from '@/utils/0x/token-blocking';
import { TokenResult } from '@/utils/0x/token-registry.types';
import { type ZoraType } from '@/utils/zora';

import { getScoreIcon } from './getScoreIcon';
import { ScoreTooltip } from './ScoreTooltip';

interface LiquidityScoreProps {
  activeToken: TokenResult;
  zoraType?: ZoraType;
  /** Replaces the default scroll-to-section behaviour. */
  onClick?: (score: number | undefined) => void;
}

export const LiquidityScore = ({ activeToken, zoraType, onClick }: LiquidityScoreProps) => {
  const { data } = useLiquidityScore([
    { address: activeToken.address, chainId: activeToken.chainId },
  ]);

  const score = getVisibleLiquidityScore(data?.data?.[0]?.score, activeToken);

  const handleScoreClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (onClick) {
      onClick(score);
      return;
    }

    const liquidityScore = document.getElementById('liquidityScore');

    if (liquidityScore) {
      liquidityScore.scrollIntoView({ behavior: 'smooth' });
    }
    ampli.liquidityButtonClicked({
      token_chain_id: activeToken.chainId,
      token_chain_name: activeToken.chainName,
      token_address: activeToken.address,
      token_symbol: activeToken.symbol,
    });
  };

  return (
    <ScoreTooltip currentScore={score} zoraType={zoraType}>
      <button
        type="button"
        aria-label={`Liquidity score: ${score ?? 'unavailable'}`}
        onClick={handleScoreClick}
        className={fontStyles({
          variant: 'LABEL_SM',
          class: [
            'items-center rounded-2xl flex justify-center h-6 px-2.5 gap-1.5',
            'outline-none bg-token-alphalight-300 hover:bg-token-alphalight-400',
            'active:bg-token-alphalight-400 focus-visible:outline-token-alphalight-500',
            'focus-visible:outline-2 focus-visible:outline-solid cursor-pointer',
          ],
        })}
      >
        {getScoreIcon(score)}
        {score ?? '-'}
      </button>
    </ScoreTooltip>
  );
};

export const getVisibleLiquidityScore = (
  score: number | undefined,
  token: Pick<TokenResult, 'tokenListsBlock'> | undefined,
) => (isTokenBlockedByTokenList(token) ? undefined : score);
